import React, {useContext} from 'react';
import PhoneNumber from "../../utilities/PhoneNumber";
import {StoreContext} from "../../../stores/StoreLoader";
import {IObservable} from "mobx";
import {observer} from "mobx-react";
import {ConnectionFactory, StaffController} from "../../../backends/YiiRoutes";
import styles from "./PersonModal.module.scss";
import {useMachine} from "@xstate/react/lib";
import {FetchMachine} from "../../machines/FetchMachine";
import FontAwesome from "../../utilities/FontAwesome";
import {buildCssUrlString, hasHTML} from "../../../utils/StringUtilities";
import {htmlToMarkdown, htmlToSlate, markdownToSlate} from "../../markdown/conversions";
import {updateUserModel} from "./UpdateUserProfile";
import InlineTextEditor from "../../utilities/InlineTextEditor";
import Markdown from "../../utilities/Markdown";
import NotificationManager from "../../notifications/NotificationManager";
import {getAvatarUrl} from "../../blocks/SchoolBlocks/blockUtils";

export function formatPhoneNumber(value: number): string {
    const phoneNumber = String(value).replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) {
        return phoneNumber;
    } else if (phoneNumberLength < 7) {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}${phoneNumber.length > 10 ? ` x${phoneNumber.slice(10)}` : ""}`;
    }
}

export const TeacherBio = observer((props: {
    blockObj: IPersonBlock,
    canEdit: boolean,
}) => {
    const {i18nStore, organizationStore} = useContext(StoreContext);
    const showStaffDirectoryEmailAddresses = organizationStore.organization.json_data.settings.appearance.showStaffDirectoryEmailAddresses;
    const personModel = props.blockObj.blockModel

    const [currentAvatarUpload, sendAvatarUpload] = useMachine(FetchMachine);

    async function handleSubmit() {
        const data = await ConnectionFactory(
            organizationStore.currentOrganization.id,
            StaffController.save(i18nStore.currentOrgSupportedLocale),
            {
                method: 'POST',
                data: {
                    'User': {
                        id: personModel.id,
                        fname: personModel.fname,
                        lname: personModel.lname,
                        title: personModel.title,
                        phone: personModel.phone,
                        email: personModel.email,
                        bio: personModel.bio,
                    },
                },
            }
        );
        if (data) {
            NotificationManager.success("Staff member updated successfully.");
        } else {
            NotificationManager.error("You have no permissions to edit this staff member!");
        }
    }

    async function handleAvatarChange(event) {
        const files = event.target.files,
            formData = new FormData(),
            userId = personModel.id;

        if (!files.length) {
            return;
        }

        formData.append('avatar', files[0]);
        formData.append('userID', userId);
        sendAvatarUpload("FETCH");

        try {
            const result = await ConnectionFactory(
                organizationStore.currentOrganization.id,
                StaffController.updatePicture(i18nStore.currentOrgSupportedLocale),
                {
                    method: "POST",
                    data: formData,
                    processData: false,
                    contentType: false,
                }
            )
            if (result.avatar) {
                props.blockObj.setAttributes({
                    blockModel: {
                        ...props.blockObj.blockModel,
                        avatarUrl: result.avatar
                    }
                })
                sendAvatarUpload("FULFILL");

                NotificationManager.success('Profile picture has been updated.');
            } else {
                sendAvatarUpload("REJECT");
                NotificationManager.error(result.error);
            }
        } catch (e) {
            sendAvatarUpload("REJECT");
            NotificationManager.error('Internal server error. Try again later.');
        }
    }

    let bio = personModel.bio || "";
    bio = hasHTML(bio) ? htmlToMarkdown(bio) : bio;

    return <>
        <div className="sb-teacher-modal-top row">
            <div className="col-md-4">
                <label htmlFor="input-teacher-avatar" id="teacher-img-placeholder"
                       className="sb-teacher-img-placeholder"
                       title="Click to upload photo">
                    <div id="teacher-avatar-picture" className="sb-circular-profile-image sb-teacher-img-size-big"
                         style={{backgroundImage: buildCssUrlString(getAvatarUrl(personModel))}}
                         title={`${personModel.fname} ${personModel.lname}`}>
                        {props.canEdit && <>
                            {currentAvatarUpload.value === "PENDING" ?
                                <div className={styles.uploading}>
                                    <div>
                                        <FontAwesome prefix={'fas'} name={'fa-spinner'} size={"3x"} spin/>
                                    </div>
                                </div> :
                                <div className="sb-teacher-select-img-container">
                                    <FontAwesome prefix={'fas'} name={'fa-camera'}/>
                                </div>}
                        </>}
                    </div>
                </label>
                {props.canEdit && <input
                    type="file"
                    className="hidden"
                    name="avatar"
                    id="input-teacher-avatar"
                    onChange={handleAvatarChange}
                    accept={"image/*"}
                />}
            </div>
            <div className="col-md-8 sb-teacher-modal-info notranslate">
                <h2>
                    {(personModel.fname || props.canEdit) && <InlineTextEditor
                        key="fname"
                        wrapperClassName={styles.editable}
                        placeholder={"First name..."}
                        canEdit={props.canEdit}
                        text={personModel.fname}
                        handleTextChange={(value) => props.blockObj.setAttributes({
                            blockModel: {
                                ...props.blockObj.blockModel,
                                fname: value
                            }
                        })}
                        isSimple={true}
                        showHoverUI={true}
                    ><span>{personModel.fname || "First name..."}</span></InlineTextEditor>} {(personModel.lname || props.canEdit) &&
                    <InlineTextEditor
                        key="lname"
                        placeholder={"Last name..."}
                        wrapperClassName={styles.editable}
                        canEdit={props.canEdit}
                        text={personModel.lname}
                        handleTextChange={(value) => props.blockObj.setAttributes({
                            blockModel: {
                                ...props.blockObj.blockModel,
                                lname: value
                            }
                        })}
                        isSimple={true}
                        showHoverUI={true}
                    ><span>{personModel.lname || "Last name..."}</span></InlineTextEditor>}
                </h2>
                {(personModel.title || props.canEdit) && <h4 key={1}>
                    <InlineTextEditor
                        key="title"
                        placeholder={"Title..."}
                        wrapperClassName={styles.editable}
                        canEdit={props.canEdit}
                        text={personModel.title || ""}
                        handleTextChange={(value) => props.blockObj.setAttributes({
                            blockModel: {
                                ...props.blockObj.blockModel,
                                title: value
                            }
                        })}
                        isSimple={true}
                        showHoverUI={true}
                    ><span>{personModel.title || "Title..."}</span></InlineTextEditor>
                </h4>}
                {(personModel.phone || props.canEdit) && <div className="sb-teacher-phone">
                    <InlineTextEditor
                        key="phone"
                        placeholder={"Enter phone..."}
                        wrapperClassName={styles.editable}
                        canEdit={props.canEdit}
                        text={personModel.phone ? formatPhoneNumber(personModel.phone) : ""}
                        handleTextChange={(value) => {
                            const phoneNumber = Number(value?.replace(/[^\d]/g, ""));
                            props.blockObj.setAttributes({
                                blockModel: {
                                    ...props.blockObj.blockModel,
                                    phone: phoneNumber
                                }
                            })
                        }}
                        isSimple={true}
                        showHoverUI={true}
                    >
                        {personModel.phone ?
                            <PhoneNumber number={formatPhoneNumber(personModel.phone)} isLinked={!props.canEdit}/> :
                            <span>Enter phone...</span>}
                    </InlineTextEditor>
                </div>}
                {showStaffDirectoryEmailAddresses && (personModel.email || props.canEdit) &&
                    <div className="sb-teacher-email">
                        <InlineTextEditor
                            key="email"
                            text={personModel.email}
                            wrapperClassName={styles.editable}
                            handleTextChange={(value) => props.blockObj.setAttributes({
                                blockModel: {
                                    ...props.blockObj.blockModel,
                                    email: value
                                }
                            })}
                            placeholder="Enter email..."
                            canEdit={props.canEdit}
                            isSimple={true}
                            showHoverUI={true}
                        >
                            <span>{personModel.email || "Enter email..."}</span>
                        </InlineTextEditor>
                    </div>}
            </div>
        </div>
        {(personModel.bio || props.canEdit) && <InlineTextEditor
            key="bio"
            canEdit={props.canEdit}
            placeholder={"Enter biography..."}
            text={bio}
            handleTextChange={(value) => props.blockObj.setAttributes({
                blockModel: {
                    ...props.blockObj.blockModel,
                    bio: value
                }})}
            wrapperClassName="sb-teacher-description"
            isSimple={false}
            showHoverUI={true}
        >
            <Markdown source={bio || "Enter biography..."} withStyles/>
        </InlineTextEditor>}
        {props.canEdit && <div className="row" style={{textAlign: "right"}}>
            <button onClick={handleSubmit}
                    className="btn sb-organization-color-element-bg">Save Bio
            </button>
        </div>}
    </>
});
